import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, Link } from "react-router-dom";
import PersonalFinancesMenu from "./PersonalFinancesMenu";
import { getSubscriptionFeatureDetails } from "../../../../../../api/payments/subscriptionsAPI";
import { getInstitutionBalances } from "../../../../../../api/finances/balancesAPI";
import LoadingPlaceholder from "../../../../../../components/ui/loading/LoadingPlaceholder";
import FiSubscriptionPlans from "../../../../financial-insights/common/FiSubscriptionPlans";
import InstitutionManagementPopup from "../../../../../../components/ui/popup/finances/InstitutionManagementModal";
import { updateUserStatus } from "../../../../../../store/redux_auth/authSlice";
import { getInstitutionSyncStates } from "../../../../../../api/finances/institutionAPI";
import { getLanguageContent } from "../../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../../utils/schemas/language/pages/personalFinancesSchema";
import { INSIGHTS_HUB_SCHEMA } from "../../../../../../utils/schemas/language/pages/insightsHubSchema";
import UpgradePlanPopup from "./UpgradePlanPopup";

function PersonalFinancesLayout() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const insightsContent = getLanguageContent(INSIGHTS_HUB_SCHEMA);
  const dispatch = useDispatch();
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [featureData, setFeatureData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // ============================================================
  // CENTRALIZED STATE FOR HOISTED INSTITUTION HANDLING
  // ============================================================
  const [isInstitutionPopupOpen, setIsInstitutionPopupOpen] = useState(false);
  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [institutionSyncStates, setInstitutionSyncStates] = useState([]);
  const [isLoadingInstitutions, setIsLoadingInstitutions] = useState(false);
  const [institutionsError, setInstitutionsError] = useState("");

  // Central Redux values
  const financialInsights = useSelector(
    (state) => state.auth.user?.financial_insights,
  );
  const isSubscribed = financialInsights?.is_subscribed;
  const hasInstitutions = financialInsights?.has_institutions;

  // 1. Initial ghost delay timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialMount(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // 2. Fetch Pricing details if not unlocked
  useEffect(() => {
    if (isSubscribed && !isUpgradePopupOpen) {
      setLoadingPricing(false);
      return;
    }

    const loadPricingMatrix = async () => {
      try {
        const response = await getSubscriptionFeatureDetails(
          "financial_insights",
        );
        if (response.ok) {
          const data = await response.json();
          setFeatureData(data.subscription || null);
        }
      } catch (err) {
        console.error(
          "Error communicating with subscription matrix backend:",
          err,
        );
      } finally {
        setLoadingPricing(false);
      }
    };

    loadPricingMatrix();
  }, [isSubscribed, isUpgradePopupOpen]);

  // ============================================================
  // SINGLE-SOURCE ORCHESTRATION CALLBACKS
  // ============================================================
  const loadInstitutions = useCallback(async () => {
    setIsLoadingInstitutions(true);
    setInstitutionsError("");
    try {
      const data = await getInstitutionBalances({ includeInactive: true });
      const fetchedList = data?.institutions || [];
      setInstitutions(fetchedList);

      if (isSubscribed) {
        const checkActiveRemaining = fetchedList.some(
          (inst) => inst.is_active === true,
        );
        if (checkActiveRemaining !== hasInstitutions) {
          dispatch(
            updateUserStatus({
              financial_insights: {
                ...financialInsights,
                has_institutions: checkActiveRemaining,
              },
            }),
          );
        }
      }
    } catch (error) {
      setInstitutionsError(
        "We could not load your connected institutions. Please try again.",
      );
    } finally {
      setIsLoadingInstitutions(false);
    }
  }, [dispatch, financialInsights, hasInstitutions, isSubscribed]);

  // Run database sync immediately whenever the centralized popup modal opens up
  useEffect(() => {
    if (!isInstitutionPopupOpen) return;
    loadInstitutions();
  }, [isInstitutionPopupOpen, loadInstitutions]);

  const loadSyncStates = useCallback(async () => {
    try {
      const syncStatesData = await getInstitutionSyncStates();
      const fetchedSyncStates = syncStatesData?.institutions || [];

      setInstitutionSyncStates(fetchedSyncStates);
    } catch (error) {
      console.error("Failed to load pipeline sync states:", error);
    }
  }, []);

  // Fire this immediately on layout initialization
  useEffect(() => {
    if (isSubscribed && hasInstitutions) {
      loadSyncStates();
    }
  }, [isSubscribed, hasInstitutions, loadSyncStates]);

  const handleInstitutionConnected = useCallback(async () => {
    dispatch(
      updateUserStatus({
        financial_insights: {
          ...financialInsights,
          has_institutions: true,
        },
      }),
    );
    await loadInstitutions();
  }, [dispatch, financialInsights, loadInstitutions]);

  const handleInstitutionRemoved = useCallback(
    async (removedInstitution) => {
      if (!removedInstitution?.uuid) {
        await loadInstitutions();
        return;
      }

      const updatedList = institutions.filter(
        (institution) => institution.uuid !== removedInstitution.uuid,
      );
      setInstitutions(updatedList);

      const checkActiveRemaining = updatedList.some(
        (inst) => inst.is_active === true,
      );
      if (!checkActiveRemaining) {
        dispatch(
          updateUserStatus({
            financial_insights: {
              ...financialInsights,
              has_institutions: false,
            },
          }),
        );
      }
    },
    [dispatch, financialInsights, institutions, loadInstitutions],
  );

  // Structural check safeguards
  if (isInitialMount || (loadingPricing && !isSubscribed)) {
    return (
      <div className="main-content-area">
        <div className="content" style={{ padding: "32px" }}>
          <LoadingPlaceholder rows={1} type="card-large" />
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDERING BLOCKS (TIER 1 & 2 INTERCEPTORS)
  // ============================================================

  // TIER 1: Unsubscribed Paywall Layout
  if (!isSubscribed) {
    return (
      <div className="main-content-area">
        <div className="content">
          <div className="internal-content-body">
            <div className="standard-card-container first-element">
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  textAlign: "center",
                }}
              >
                <div>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/lock_dark.svg`}
                    alt="Locked"
                    style={{ width: "64px", height: "64px" }}
                  />
                </div>
                <h2 style={{ color: "#40464b", fontWeight: "500" }}>
                  Personal Finances is locked
                </h2>
                <p>
                  Unlock access to automated transaction histories, personalized
                  ledgers, spending habits breakdowns, and real-time active
                  budget trackers by choosing a plan below.
                </p>
              </div>
            </div>
            <FiSubscriptionPlans
              featureData={featureData}
              content={insightsContent}
            />
          </div>
        </div>
      </div>
    );
  }

  // TIER 2: Subscribed but Empty-Onboarding State
  if (isSubscribed && !hasInstitutions) {
    return (
      <div className="main-content-area">
        <div className="content">
          <div className="internal-content-body">
            <div className="standard-card-container first-element">
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  textAlign: "center",
                }}
              >
                <div>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/institutions.svg`}
                    alt="Connect Bank"
                    style={{ width: "64px", height: "64px", opacity: 0.8 }}
                  />
                </div>
                <h2
                  style={{ color: "#40464b", fontWeight: "500", marginTop: 0 }}
                >
                  Connect an Institution
                </h2>
                <p>
                  Your plan is active! To begin generating your custom financial
                  overview summaries, active spendings trackers, and budgets
                  dashboards, securely link your financial accounts first.
                </p>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsInstitutionPopupOpen(true);
                    }}
                    className="cta-button primary rounded"
                  >
                    Connect Your First Institution
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isInstitutionPopupOpen && (
          <InstitutionManagementPopup
            institutions={institutions}
            isLoading={isLoadingInstitutions}
            errorMessage={institutionsError}
            onInstitutionConnected={handleInstitutionConnected}
            onInstitutionRemoved={handleInstitutionRemoved}
            copy={content.institutions.modal}
            onClose={() => setIsInstitutionPopupOpen(false)}
          />
        )}
      </div>
    );
  }

  // TIER 3: Active Dashboard Shell Frame
  return (
    <div className="main-content-area">
      <div className="content content-gray">
        <div className="ich-wrapper">
          <div className="internal-content-header">
            {/* HOIST ACTION: Hand the click handler callback down seamlessly to your menu */}
            <PersonalFinancesMenu
              isLoading={isInitialMount}
              onOpenInstitutions={() => setIsInstitutionPopupOpen(true)}
              onOpenUpgrade={() => setIsUpgradePopupOpen(true)}
            />
          </div>
        </div>
        <div className="internal-content-body">
          <Outlet context={{ institutionSyncStates, loadSyncStates }} />
        </div>
      </div>

      {/* SINGLE MOUNT POINT: This portal serves both the layout onboards and the nested header tabs menu button actions */}
      {isInstitutionPopupOpen && (
        <InstitutionManagementPopup
          institutions={institutions}
          isLoading={isLoadingInstitutions}
          errorMessage={institutionsError}
          onInstitutionConnected={handleInstitutionConnected}
          onInstitutionRemoved={handleInstitutionRemoved}
          copy={content.institutions.modal}
          onClose={() => setIsInstitutionPopupOpen(false)}
        />
      )}

      {/*UPDATED POPUP INJECTION POINT */}
      <UpgradePlanPopup
        isOpen={isUpgradePopupOpen}
        onClose={() => setIsUpgradePopupOpen(false)}
        featureData={featureData}
        content={insightsContent}
        currentSubscriptionUuid={financialInsights?.subscription_uuid}
      />
    </div>
  );
}

export default PersonalFinancesLayout;
